import { useState, useEffect, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { flightsSearchHandleFetch } from './SearchTabs/flightsSearchHandleFetch';
import { runFlightsSearch } from './SearchTabs/runFlightsSearch';

import useVersionControl from '@shared/hooks/useVersionControl';
import { FLIGHTS_SEARCH } from '@shared/helpers/routes';
import { setSearchFlights } from '@shared/redux/actions';

export const useFlightSearchLogic = ({
  setSearchResults,
  setIsLoadingResults,
  isInUSD,
  onRegisterSearchRef,
  ref,
  searchType,
  isLoadingResults,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const shouldRefetch = location?.state?.refetch || false;

  const { checkVersion } = useVersionControl(FLIGHTS_SEARCH);

  const infoIsThreeDaysSearch = useSelector(store => store.isThreeDaysSearch);
  const user = useSelector(store => store.userInfo);
  const flightsSearch = useSelector(store => store.flightsSearch);

  const infoFlightsSearch = flightsSearch?.[flightsSearch?.currentTripType];

  const [hasErrorMessage, setHasErrorMessage] = useState(null);
  const [showPassengers, setShowPassengers] = useState(false);

  const [searchPax, setSearchPax] = useState({
    adults: infoFlightsSearch?.adults_qty || 1,
    childs: infoFlightsSearch?.childs || [],
  });

  const [flightClass, setFlightClass] = useState(
    infoFlightsSearch?.cabin_type
      ? Number.parseInt(infoFlightsSearch.cabin_type)
      : 1
  );

  const [airSectionRow, setAirSectionRow] = useState(
    infoFlightsSearch?.dataTripLegs || []
  );

  useEffect(() => {
    if (!infoFlightsSearch) return;

    setAirSectionRow(infoFlightsSearch?.dataTripLegs || []);
    setSearchPax({
      adults: infoFlightsSearch?.adults_qty || 1,
      childs: infoFlightsSearch?.childs || [],
    });
    setFlightClass(
      infoFlightsSearch?.cabin_type
        ? Number.parseInt(infoFlightsSearch.cabin_type)
        : 1
    );

    setHasErrorMessage(null);
  }, [
    infoFlightsSearch?.dataTripLegs,
    infoFlightsSearch?.adults_qty,
    infoFlightsSearch?.childs,
    infoFlightsSearch?.cabin_type,
  ]);

  useEffect(() => {
    if (onRegisterSearchRef && ref?.current) {
      onRegisterSearchRef(ref.current);
    }
  }, [onRegisterSearchRef, ref]);

  const executeSearch = async ({
    new_go_date = null,
    new_back_date = null,
  } = {}) => {
    if (isLoadingResults) {
      return;
    }

    // Preparamos el body de la búsqueda
    const preparedBody = flightsSearchHandleFetch({
      flightClass,
      searchPax,
      isInUSD,
      airSectionRow,
      setHasErrorMessage,
      dispatch,
      user,
      searchType,
      new_go_date,
      new_back_date,
    });

    if (!preparedBody) {
      return;
    }

    // 🚀 Guardamos los datos en Redux ANTES de navegar
    dispatch(
      setSearchFlights({ ...preparedBody, trip_type: preparedBody.trip_type })
    );

    // Si no estamos en la ruta de búsqueda, navegamos
    if (location.pathname !== FLIGHTS_SEARCH) {
      history.push(FLIGHTS_SEARCH, { refetch: true });
      return;
    }

    // Ejecutamos la búsqueda real
    try {
      await runFlightsSearch({
        dispatch,
        setSearchResults,
        setIsLoadingResults,
        preparedBody,
        checkVersion,
      });
      setHasErrorMessage(null);
    } catch (error) {
      console.error('❌ Error en runFlightsSearch', error);
      setHasErrorMessage('Ocurrió un error al buscar vuelos.');
    }
  };

  useEffect(() => {
    if (shouldRefetch) {
      executeSearch();
      history.replace({ ...location, state: {} });
    }
  }, [shouldRefetch]);

  useEffect(() => {
    const goDate = infoIsThreeDaysSearch?.new_go_date || null;
    const backDate = infoIsThreeDaysSearch?.new_back_date || null;

    const shouldSearch =
      (searchType === 'OneWay' && goDate) ||
      (searchType === 'RoundTrip' && goDate && backDate);

    if (!shouldSearch) return;

    const updatedAirSection = [...airSectionRow];

    if (updatedAirSection[0]) updatedAirSection[0].date = goDate;
    if (searchType === 'RoundTrip' && updatedAirSection[1]) {
      updatedAirSection[1].date = backDate;
    }

    setAirSectionRow(updatedAirSection);
    executeSearch({
      new_go_date: goDate,
      new_back_date: backDate,
    });
  }, [
    infoIsThreeDaysSearch?.new_go_date,
    infoIsThreeDaysSearch?.new_back_date,
  ]);

  const handleClick = event => setShowPassengers(event.currentTarget);
  const handleClose = () => setShowPassengers(null);

  useImperativeHandle(ref, () => ({
    doSearch: () => {
      executeSearch();
    },
  }));

  return {
    showPassengers,
    handleClick,
    handleClose,
    searchPax,
    setSearchPax,
    flightClass,
    setFlightClass,
    hasErrorMessage,
    airSectionRow,
    setAirSectionRow,
    executeSearch,
    infoIsThreeDaysSearch,
  };
};
